import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CheckCircle, XCircle, User, Building2, Stethoscope } from 'lucide-react';
import { PlateMindLogo } from '../components/PlateMindLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const codeFromUrl = searchParams.get('code') || '';

  const [code, setCode] = useState(codeFromUrl);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(!!codeFromUrl);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Validate code from URL on mount
  useEffect(() => {
    if (codeFromUrl) {
      validateCode(codeFromUrl);
    }
  }, [codeFromUrl]);

  const validateCode = async (inviteCode) => {
    setValidating(true);
    setError('');
    setInvitation(null);

    try {
      const response = await fetch(`${API_URL}/api/invitations/validate/${inviteCode.toUpperCase()}`);
      const data = await response.json();

      if (data.valid) {
        setInvitation(data);
      } else {
        setError(data.error || 'Invalid invitation code');
      }
    } catch (err) {
      setError('Failed to validate invitation code');
    } finally {
      setValidating(false);
    }
  };

  const handleAccept = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      // Redirect to login/signup with return URL
      navigate(`/login?redirect=/accept-invite&code=${code}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ invitation_code: code.toUpperCase() })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(data.detail || 'Failed to accept invitation');
      }
    } catch (err) {
      setError('Failed to accept invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (code.trim()) {
      validateCode(code.trim());
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <PlateMindLogo size={80} />
        </div>

        {success ? (
          // Success state
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                You're Connected!
              </h2>
              <p className="text-gray-600 mb-4">
                You've successfully joined {invitation?.partner_name}'s care team with{' '}
                <span className="font-semibold text-orange-600">{invitation?.dietitian_name}</span>.
              </p>
              <p className="text-sm text-gray-500">
                Redirecting to dashboard...
              </p>
            </CardContent>
          </Card>
        ) : !invitation && !codeFromUrl ? (
          // Enter code state
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Enter Invitation Code</CardTitle>
              <CardDescription>
                Enter the code your dietitian shared with you
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCodeSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Invitation Code</Label>
                  <Input
                    id="code"
                    data-testid="invite-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="XXXXXXXX"
                    className="text-center text-2xl tracking-widest font-mono"
                    maxLength={8}
                  />
                </div>

                {error && (
                  <Alert variant="destructive">
                    <XCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  data-testid="validate-code-btn"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={validating || code.length < 6}
                >
                  {validating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col text-center text-sm text-gray-500">
              <p>Don't have a code?</p>
              <Button
                variant="link"
                className="text-orange-600 p-0 h-auto"
                onClick={() => navigate('/signup')}
              >
                Sign up for a free account
              </Button>
            </CardFooter>
          </Card>
        ) : validating ? (
          // Loading state
          <Card className="border-0 shadow-xl">
            <CardContent className="py-12 text-center">
              <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Validating invitation...</p>
            </CardContent>
          </Card>
        ) : invitation ? (
          // Valid invitation state
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center border-b pb-6">
              <CardTitle className="text-2xl">You're Invited!</CardTitle>
              <CardDescription>
                Join your care team on PlateMind
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Dietitian info */}
              <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Your Dietitian</p>
                  <p className="font-semibold text-gray-900">{invitation.dietitian_name}</p>
                </div>
              </div>

              {/* Partner info */}
              {invitation.partner_name && (
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Organization</p>
                    <p className="font-semibold text-gray-900">{invitation.partner_name}</p>
                  </div>
                </div>
              )}

              {/* Personal message */}
              {invitation.message && (
                <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-orange-400">
                  <p className="text-gray-600 italic">"{invitation.message}"</p>
                  <p className="text-sm text-gray-500 mt-2">— {invitation.dietitian_name}</p>
                </div>
              )}

              {/* Patient info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Invited as</p>
                  <p className="font-semibold text-gray-900">{invitation.patient_name}</p>
                  <p className="text-sm text-gray-500">{invitation.patient_email}</p>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-3 pt-6 border-t">
              <Button
                data-testid="accept-invite-btn"
                className="w-full bg-orange-500 hover:bg-orange-600"
                onClick={handleAccept}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : isLoggedIn ? (
                  'Accept & Connect'
                ) : (
                  'Login to Accept'
                )}
              </Button>
              {!isLoggedIn && (
                <p className="text-sm text-gray-500 text-center">
                  Don't have an account?{' '}
                  <Button
                    variant="link"
                    className="text-orange-600 p-0 h-auto"
                    onClick={() => navigate(`/signup?code=${code}&email=${encodeURIComponent(invitation.patient_email)}&name=${encodeURIComponent(invitation.patient_name)}`)}
                  >
                    Sign up here
                  </Button>
                </p>
              )}
            </CardFooter>
          </Card>
        ) : (
          // Error state
          <Card className="border-0 shadow-xl">
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Invalid Invitation
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button
                variant="outline"
                onClick={() => {
                  setCode('');
                  setError('');
                  setInvitation(null);
                }}
              >
                Try Another Code
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
